<template>
  <div class="login-auth-page">
    <div class="auth-video" aria-hidden="true">
      <video autoplay muted loop playsinline webkit-playsinline preload="auto">
        <source src="/login-hero.mp4" type="video/mp4" />
      </video>
      <div class="auth-shade"></div>
    </div>

    <div class="auth-langs">
      <div class="top-actions">
        <div class="menu" :class="{ open: showLangMenu }">
          <button class="pill" type="button" @click.stop="toggleLangMenu">
            <span class="pill-lab">{{ currentLanguage.label }}</span>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M6 9l6 6 6-6"></path>
            </svg>
          </button>

          <div v-if="showLangMenu" class="menu-pop lang-pop">
            <button
              v-for="item in languageOptions"
              :key="item.code"
              type="button"
              :class="{ active: displayLang === item.code }"
              @click.stop="changeLang(item)"
            >
              <span>{{ item.short }}</span>
              <b>{{ item.fullLabel }}</b>
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="auth">
      <div class="auth-brand">
        <div class="brand-logo">
          {{ brandName }}
        </div>
        <p>{{ copy.slogan }}</p>
      </div>

      <div class="auth-card">
        <h1>{{ copy.welcome }}</h1>
        <form autocomplete="off" @submit.prevent="doLogin">
          <div class="field">
            <label>{{ copy.accountLabel }}</label>
            <input
              v-model.trim="username"
              :placeholder="copy.accountPlaceholder"
              autocomplete="username"
            />
          </div>
          <div class="field">
            <label>{{ copy.passwordLabel }}</label>
            <input
              v-model="password"
              type="password"
              :placeholder="copy.passwordPlaceholder"
              autocomplete="current-password"
            />
          </div>
          <button class="auth-submit" type="submit" :disabled="loading">
            {{ loading ? copy.loading : copy.submit }}
          </button>
        </form>
        <button class="auth-link" type="button" @click="toRegister">
          {{ copy.register }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import {
  DEFAULT_LANGUAGE_OPTION,
  LANGUAGE_OPTIONS,
  applyAppLanguage,
  findLanguageOption,
  readSavedLanguageOption,
} from "@/common/languages";
import { resolveLanguageOptions } from "@/common/kefu";
import { storeAuthSession } from "@/common/session";

export default {
  data() {
    return {
      username: "",
      password: "",
      loading: false,
      displayLang: "zh_cn",
      languageOptions: LANGUAGE_OPTIONS,
      showLangMenu: false,
    };
  },
  computed: {
    baseInfo() {
      return this.$store.getters.getBaseInfo || {};
    },
    brandName() {
      const ui = this.baseInfo.ui || {};
      return ui.brand || this.baseInfo.name || "OMN";
    },
    copy() {
      return {
        slogan: this.$t("auth.login_slogan"),
        welcome: this.$t("auth.login_welcome"),
        accountLabel: this.$t("auth.login_account_label"),
        accountPlaceholder: this.$t("auth.username_place"),
        passwordLabel: this.$t("auth.login_password_label"),
        passwordPlaceholder: this.$t("auth.pwd_place"),
        submit: this.$t("auth.login_submit"),
        register: this.$t("auth.login_register_link"),
        loading: this.$t("auth.login_loading"),
        failed: this.$t("auth.login_failed"),
        networkError: this.$t("auth.network_error"),
      };
    },
    currentLanguage() {
      return findLanguageOption(this.displayLang) || this.languageOptions[0] || DEFAULT_LANGUAGE_OPTION;
    },
  },
  watch: {
    baseInfo: {
      deep: true,
      handler(info) {
        this.languageOptions = resolveLanguageOptions(info, LANGUAGE_OPTIONS);
      },
    },
  },
  created() {
    if (localStorage.getItem("token")) {
      this.$router.replace("/Mine");
      return;
    }
    this.languageOptions = resolveLanguageOptions(this.baseInfo, LANGUAGE_OPTIONS);
    this.initLanguage();
  },
  mounted() {
    window.addEventListener("click", this.handleWindowClick);
  },
  beforeDestroy() {
    window.removeEventListener("click", this.handleWindowClick);
  },
  methods: {
    initLanguage() {
      this.applyLanguage(readSavedLanguageOption());
    },
    applyLanguage(language, closeMenu = true) {
      const next = applyAppLanguage(this, language);
      this.displayLang = next.code;
      if (closeMenu) {
        this.showLangMenu = false;
      }
    },
    changeLang(language) {
      this.applyLanguage(language);
    },
    toggleLangMenu() {
      this.showLangMenu = !this.showLangMenu;
    },
    handleWindowClick() {
      this.showLangMenu = false;
    },
    toRegister() {
      this.$router.push("/Register");
    },
    async doLogin() {
      if (!this.username) {
        this.$toast(this.copy.accountPlaceholder);
        return;
      }
      if (!this.password) {
        this.$toast(this.copy.passwordPlaceholder);
        return;
      }

      this.loading = true;
      try {
        const res = await this.$http({
          url: "member_login",
          method: "post",
          data: {
            username: this.username,
            password: this.password,
            lang: this.currentLanguage.appLang,
          },
        });
        if (res && res.code === 200) {
          const credential = storeAuthSession(res.data);
          if (!credential) {
            this.$toast(this.copy.failed);
            return;
          }
          this.$toast.success(res.msg);
          this.$router.replace("/Mine");
          return;
        }
        this.$toast((res && res.msg) || this.copy.failed);
      } catch (error) {
        this.$toast(this.copy.networkError);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="less" scoped>
.login-auth-page {
  position: fixed;
  inset: 0;
  overflow-x: hidden;
  overflow-y: auto;
  background: #08060c;
  color: #fff7fb;
}

.auth-video {
  position: fixed;
  inset: 0;
  z-index: 0;
  overflow: hidden;
  pointer-events: none;
  background: radial-gradient(circle at 50% 20%, #34162d 0, #100913 46%, #07050a 100%);
}

.auth-video video {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  filter: saturate(0.9) contrast(1.03);
  transform: scale(1.012);
}

.auth-shade {
  position: absolute;
  inset: 0;
  background:
    linear-gradient(180deg, rgba(7, 4, 10, 0.42) 0%, rgba(8, 5, 11, 0.28) 28%, rgba(8, 5, 11, 0.58) 67%, rgba(7, 4, 10, 0.9) 100%),
    radial-gradient(circle at 50% 15%, rgba(255, 63, 120, 0.13), transparent 40%);
}

.auth-langs {
  position: relative;
  z-index: 3;
  width: min(100%, 460px);
  margin: 0 auto;
  padding: max(14px, env(safe-area-inset-top)) 18px 0;
}

.top-actions {
  margin-left: auto;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.45rem;
}

.menu {
  position: relative;
}

.pill {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  height: 2.35rem;
  max-width: 100%;
  padding: 0 0.8rem;
  border: 1px solid rgba(255, 255, 255, 0.085);
  border-radius: 1.2rem;
  background: rgba(255, 255, 255, 0.055);
  color: #fff7fb;
  font-size: 0.82rem;
  font-weight: 800;
  box-shadow: 0 0.5rem 1.4rem rgba(0, 0, 0, 0.18);
}

.pill svg {
  width: 0.9rem;
  height: 0.9rem;
  flex: 0 0 auto;
}

.pill-lab {
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.menu-pop {
  position: absolute;
  top: calc(100% + 0.45rem);
  right: 0;
  min-width: 8.8rem;
  padding: 0.35rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 1rem;
  background: rgba(24, 16, 31, 0.98);
  box-shadow: 0 1.2rem 3rem rgba(0, 0, 0, 0.4);
  z-index: 30;
}

.menu-pop button {
  width: 100%;
  padding: 0.7rem 0.8rem;
  border: 0;
  border-radius: 0.8rem;
  background: transparent;
  color: #fff7fb;
  font-size: 0.82rem;
  font-weight: 700;
  text-align: left;
}

.menu-pop button.active {
  background: rgba(255, 63, 120, 0.14);
  color: #ff91b1;
}

.lang-pop {
  min-width: 11rem;
}

.lang-pop button {
  display: grid;
  grid-template-columns: 2rem 1fr;
  align-items: center;
  gap: 0.5rem;
}

.lang-pop button span {
  width: 2rem;
  height: 2rem;
  border-radius: 0.7rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.06);
  color: #ff9bb8;
  font-size: 0.62rem;
  font-weight: 950;
}

.lang-pop button b {
  font-size: 0.76rem;
}

.auth {
  position: relative;
  z-index: 1;
  width: min(100%, 460px);
  min-height: calc(100vh - 44px);
  min-height: calc(100dvh - 44px);
  margin: 0 auto;
  padding: 10px 22px max(28px, env(safe-area-inset-bottom));
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.auth-brand {
  margin: 10px 0 24px;
  text-align: center;
  text-shadow: 0 2px 18px rgba(0, 0, 0, 0.6);
}

.brand-logo {
  display: inline-flex;
  align-items: center;
  font-size: 40px;
  line-height: 1;
  font-weight: 950;
  letter-spacing: -1.6px;
  color: #fff;
  filter: drop-shadow(0 8px 22px rgba(0, 0, 0, 0.46));
}

.brand-logo em {
  margin-left: 3px;
  padding: 4px 8px 5px;
  border-radius: 10px;
  color: #190b12;
  font-style: normal;
  background: linear-gradient(135deg, #ff739f, #ff3f78);
  box-shadow: 0 7px 20px rgba(255, 63, 120, 0.28);
}

.auth-brand p {
  margin: 13px 0 0;
  color: rgba(255, 245, 250, 0.82);
  font-size: 12px;
}

.auth-card {
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 25px;
  padding: 21px;
  background: linear-gradient(145deg, rgba(27, 16, 31, 0.88), rgba(13, 8, 17, 0.82));
  box-shadow: 0 24px 65px rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(18px) saturate(1.1);
  -webkit-backdrop-filter: blur(18px) saturate(1.1);
}

.auth-card h1 {
  margin: 0 0 18px;
  font-size: 21px;
}

.field {
  margin-bottom: 14px;
}

.field label {
  display: block;
  margin: 0 0 7px;
  color: #cfc1cc;
  font-size: 11px;
}

.field input {
  width: 100%;
  height: 49px;
  padding: 0 14px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 15px;
  background: rgba(12, 8, 16, 0.66);
  color: #fff;
  font-size: 14px;
  outline: none;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}

.field input::placeholder {
  color: #7d7280;
}

.field input:focus {
  border-color: rgba(255, 88, 142, 0.65);
  box-shadow: 0 0 0 3px rgba(255, 63, 120, 0.08);
}

.auth-submit {
  width: 100%;
  height: 49px;
  margin-top: 7px;
  border: 0;
  border-radius: 16px;
  background: linear-gradient(135deg, #ff6d9c, #ff3f78);
  color: #fff;
  font-size: 15px;
  font-weight: 950;
  box-shadow: 0 10px 25px rgba(255, 63, 120, 0.24);
}

.auth-submit[disabled] {
  opacity: 0.45;
}

.auth-link {
  display: block;
  width: 100%;
  margin-top: 18px;
  border: 0;
  background: transparent;
  color: #ffacc4;
  font-size: 12px;
  text-align: center;
  text-shadow: 0 2px 12px rgba(0, 0, 0, 0.4);
}

button {
  cursor: pointer;
}

@media (min-width: 768px) {
  .login-auth-page {
    padding: 0 16px;
  }

  .auth-langs {
    width: auto;
    max-width: none;
    margin: 0;
    padding: 18px 24px 0;
  }

  .pill {
    height: 2.625rem;
    padding: 0 1rem;
    border-radius: 1.3rem;
    font-size: 0.95rem;
  }

  .pill svg {
    width: 1rem;
    height: 1rem;
  }

  .lang-pop {
    min-width: 12rem;
  }

  .lang-pop button span {
    width: 2.1rem;
    height: 2.1rem;
    font-size: 0.7rem;
  }

  .lang-pop button b {
    font-size: 0.82rem;
  }

  .auth {
    width: 460px;
    min-height: calc(100vh - 60px);
    padding: 16px 22px 28px;
  }

  .auth-brand {
    margin: 12px 0 24px;
  }

  .brand-logo {
    font-size: 76px;
  }

  .brand-logo em {
    margin-left: 6px;
    padding: 8px 14px 10px;
    border-radius: 18px;
  }

  .auth-brand p {
    margin-top: 14px;
    font-size: 18px;
  }

  .auth-card {
    border-radius: 30px;
    padding: 40px 38px 28px;
  }

  .auth-card h1 {
    margin-bottom: 28px;
    font-size: 34px;
  }

  .field {
    margin-bottom: 22px;
  }

  .field label {
    margin-bottom: 10px;
    font-size: 18px;
  }

  .field input {
    height: 72px;
    padding: 0 26px;
    border-radius: 20px;
    font-size: 24px;
  }

  .auth-submit {
    height: 72px;
    margin-top: 12px;
    border-radius: 20px;
    font-size: 24px;
  }

  .auth-link {
    margin-top: 20px;
    font-size: 18px;
  }
}

@media (max-height: 680px) {
  .auth {
    justify-content: flex-start;
    padding-top: 8px;
  }

  .auth-brand {
    margin: 8px 0 16px;
  }

  .brand-logo {
    font-size: 34px;
  }

  .auth-card {
    padding: 18px;
  }

  .field {
    margin-bottom: 10px;
  }

  .field input,
  .auth-submit {
    height: 46px;
  }
}

@media (max-width: 767px) {
  .auth-langs {
    width: 100%;
    max-width: 375px;
    padding: max(14px, env(safe-area-inset-top)) 16px 0;
  }

  .auth {
    width: 100%;
    max-width: 375px;
    min-height: calc(100vh - 44px);
    min-height: calc(100dvh - 44px);
    padding: 12px 20px max(28px, env(safe-area-inset-bottom));
  }

  .auth-brand {
    margin: 14px 0 22px;
  }

  .brand-logo {
    font-size: 58px;
  }

  .brand-logo em {
    margin-left: 4px;
    padding: 5px 9px 7px;
    border-radius: 12px;
  }

  .auth-brand p {
    margin-top: 10px;
    font-size: 13px;
  }

  .auth-card {
    border-radius: 24px;
    padding: 24px 18px 20px;
  }

  .auth-card h1 {
    margin-bottom: 22px;
    font-size: 22px;
  }

  .field {
    margin-bottom: 16px;
  }

  .field label {
    margin-bottom: 8px;
    font-size: 13px;
  }

  .field input {
    height: 50px;
    padding: 0 16px;
    border-radius: 16px;
    font-size: 16px;
  }

  .auth-submit {
    height: 50px;
    margin-top: 8px;
    border-radius: 16px;
    font-size: 17px;
  }

  .auth-link {
    margin-top: 16px;
    font-size: 13px;
  }
}

@media (max-width: 390px) {
  .auth-langs,
  .auth {
    max-width: 100%;
  }

  .brand-logo {
    font-size: 50px;
  }

  .auth-card {
    padding: 22px 16px 18px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .auth-video video {
    display: none;
  }
}
</style>
